<template>
  <v-navigation-drawer v-model="drawer" right temporary fixed width="400" class="smart-quote-audit-drawer">
    <div class="pa-5">
      <div class="d-flex align-center justify-space-between mb-1">
        <span class="text-h6">{{ 'smartQuoteAuditTrail'|localize }}</span>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
      <div v-if="subtitle" class="text-caption grey--text mb-4 smart-quote-audit-subtitle">{{ subtitle }}</div>

      <v-divider class="mb-4" />

      <div v-if="loading" class="d-flex justify-center py-9">
        <v-progress-circular indeterminate color="primary" />
      </div>
      <div v-else-if="!entries.length" class="grey--text text-body-2 text-center py-6">
        {{ 'smartQuoteAuditEmpty'|localize }}
      </div>
      <v-timeline v-else dense align-top class="smart-quote-audit-timeline">
        <v-timeline-item
          v-for="entry in entries"
          :key="entry.id"
          small
          fill-dot
          :color="entryMeta(entry).color"
        >
          <template v-slot:icon>
            <v-icon color="white" size="14">{{ entryMeta(entry).icon }}</v-icon>
          </template>
          <div class="d-flex">
            <div class="text-caption grey--text smart-quote-audit-time">{{ formatTime(entry.createdAt) }}</div>
            <div class="smart-quote-audit-content">
              <div class="font-weight-medium text-body-2">{{ entryTitle(entry) }}</div>
              <div v-for="(line, idx) in entryDetailLines(entry)" :key="idx" class="text-caption grey--text">{{ line }}</div>
            </div>
          </div>
        </v-timeline-item>
      </v-timeline>
    </div>

    <template v-slot:append>
      <div class="pa-4 text-caption grey--text d-flex align-center smart-quote-audit-footer">
        <v-icon size="14" left color="grey">mdi-clock-outline</v-icon>
        {{ interpolate('smartQuoteAuditTimezoneNote', {tz: timezone}) }}
      </div>
    </template>
  </v-navigation-drawer>
</template>

<script>
  import localize from '@/filters/localize';
  import methods from '@/helpers/methods';
  import api from '@/api/api';

  const ACTION_META = {
    fetched: {icon: 'mdi-email-outline', color: 'blue', labelKey: 'smartQuoteAuditEmailDetected'},
    docupipe_extracted: {icon: 'mdi-paperclip', color: 'light-blue', labelKey: 'smartQuoteAuditPdfReceived'},
    processed: {icon: 'mdi-creation', color: 'blue', labelKey: 'smartQuoteAuditExtractionCompleted'},
    proposal_created: {icon: 'mdi-check-circle', color: 'green', labelKey: 'smartQuoteAuditQuoteCreated'},
    creation_skipped_duplicate: {icon: 'mdi-content-duplicate', color: 'blue-grey', labelKey: 'smartQuoteAuditDuplicateSkipped'},
    processing_failed: {icon: 'mdi-close-circle', color: 'red', labelKey: 'smartQuoteActionProcessingFailed'},
    creation_failed: {icon: 'mdi-close-circle', color: 'red', labelKey: 'smartQuoteActionCreationFailed'},
    creation_blocked: {icon: 'mdi-alert-circle', color: 'orange', labelKey: 'smartQuoteActionCreationBlocked'},
    sender_remembered: {icon: 'mdi-bookmark', color: 'purple', labelKey: 'smartQuoteActionSenderRemembered'},
    notification_sent: {icon: 'mdi-bell', color: 'indigo', labelKey: 'smartQuoteActionNotificationSent'},
    push_received: {icon: 'mdi-arrow-down-bold', color: 'grey', labelKey: 'smartQuoteActionPushReceived'},
    sync_failed: {icon: 'mdi-sync-alert', color: 'red', labelKey: 'smartQuoteActionSyncFailed'},
  };

  export default {
    name: 'SmartQuoteAuditTrailDrawer',

    data() {
      return {
        drawer: false,
        loading: false,
        proposalId: null,
        attachment: null,
        entries: [],
      };
    },

    computed: {
      subtitle() {
        if (!this.attachment) return '';
        return [this.attachment.carrierName, this.attachment.requestId].filter(Boolean).join(' • ');
      },
      timezone() {
        try {
          return Intl.DateTimeFormat().resolvedOptions().timeZone;
        } catch (e) {
          return '';
        }
      },
    },

    methods: {
      open(proposalId) {
        this.proposalId = proposalId;
        this.attachment = null;
        this.entries = [];
        this.drawer = true;
        this.load();
      },
      close() {
        this.drawer = false;
      },
      async load() {
        if (!this.proposalId) return;
        this.loading = true;
        try {
          let response = await api.getSmartQuoteProposalAudit(this.proposalId);
          this.attachment = response.data.attachment || null;
          this.entries = response.data.items || [];
        } catch (e) {
          methods.showGlobalSnackbar('error', methods.getErrors(e.response));
        } finally {
          this.loading = false;
        }
      },
      interpolate(key, params) {
        let str = localize(key);
        Object.keys(params).forEach(param => {
          str = str.replace(`{${param}}`, params[param]);
        });
        return str;
      },
      formatTime(iso) {
        if (!iso) return '—';
        return new Date(iso).toTimeString().slice(0, 5);
      },
      entryMeta(entry) {
        let meta = ACTION_META[entry.action];
        if (meta) return meta;
        if (entry.actorType === 'admin') return {icon: 'mdi-account', color: 'purple'};
        return {icon: 'mdi-circle-small', color: 'grey'};
      },
      entryTitle(entry) {
        let meta = ACTION_META[entry.action];
        if (meta) return localize(meta.labelKey);
        if (entry.actorType === 'admin') return localize('smartQuoteAuditUserReviewed');
        return (entry.action || '').replace(/_/g, ' ');
      },
      entryDetailLines(entry) {
        let d = entry.details || {};
        let lines = [];

        switch (entry.action) {
          case 'fetched':
            if (d.senderEmail) lines.push(`${localize('smartQuoteAuditFrom')}: ${d.senderEmail}`);
            if (d.threadId) lines.push(`${localize('smartQuoteAuditThreadId')}: ${d.threadId}`);
            if (d.filename) lines.push(`${localize('smartQuoteAuditFile')}: ${d.filename}`);
            break;
          case 'docupipe_extracted':
            if (d.filename) lines.push(`${localize('smartQuoteAuditFile')}: ${d.filename}`);
            if (d.sizeBytes != null) lines.push(`${localize('smartQuoteAuditSize')}: ${Math.round(d.sizeBytes / 1024)} KB`);
            if (d.reused_from_attachment_id != null) {
              lines.push(`${localize('smartQuoteAuditReusedFrom')} #${d.reused_from_attachment_id}`);
            }
            break;
          case 'processed':
            if (d.price != null) lines.push(`${localize('price')}: ${d.price} ${d.currency || ''}`.trim());
            if (d.transitDays != null) lines.push(`${localize('smartQuoteAuditTransitTime')}: ${d.transitDays} ${localize('days')}`);
            if (d.matchPercent != null) lines.push(`${localize('matchPercent')}: ${d.matchPercent}%`);
            break;
          case 'proposal_created':
            if (d.proposalId) lines.push(`#${d.proposalId}`);
            if (d.via) lines.push(localize(d.via === 'auto' ? 'smartQuoteCreatedViaAuto' : 'smartQuoteCreatedViaManual'));
            break;
          case 'creation_skipped_duplicate':
            if (d.existingProposalId) lines.push(`${localize('smartQuoteAuditExistingProposal')} #${d.existingProposalId}`);
            if (d.transportMode || d.price != null) {
              let priceStr = d.price != null ? `${d.price} ${d.currency || ''}`.trim() : '';
              lines.push([d.transportMode, priceStr].filter(Boolean).join(', '));
            }
            break;
          case 'processing_failed':
          case 'creation_failed':
            if (d.error) lines.push(d.error);
            break;
          case 'sync_failed':
            if (d.error || d.reason) lines.push(d.error || d.reason);
            break;
          case 'creation_blocked':
            if (d.reason) lines.push(d.reason);
            break;
          case 'sender_remembered':
          case 'sender_updated':
          case 'sender_enabled':
          case 'sender_disabled':
          case 'sender_deleted':
            if (d.senderEmail) lines.push(d.senderEmail);
            break;
          case 'notification_sent':
            if (Array.isArray(d.recipients)) lines.push(d.recipients.join(', '));
            break;
          case 'push_received':
            if (d.notifiedHistoryId != null) lines.push(`${localize('smartQuoteAuditHistoryId')} #${d.notifiedHistoryId}`);
            break;
          default:
            break;
        }

        if (entry.actorType === 'admin' && entry.actorName) {
          lines.push(`${localize('smartQuoteAuditReviewedBy')}: ${entry.actorName}`);
          if (d.summary) lines.push(d.summary);
        }

        return lines;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .smart-quote-audit-subtitle {
    word-break: break-word;
  }

  .smart-quote-audit-time {
    width: 44px;
    flex: none;
    padding-top: 2px;
  }

  .smart-quote-audit-content {
    flex: 1 1 auto;
    min-width: 0;
    word-break: break-word;
  }

  ::v-deep .smart-quote-audit-drawer .v-navigation-drawer__content {
    overflow-x: hidden;
    display: flex;
    flex-direction: column;
  }

  .smart-quote-audit-footer {
    border-top: 1px solid rgba(0, 0, 0, 0.08);
  }
</style>
